// src/pages/Clinic/ClinicDashboard.jsx
import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Avatar, IconButton, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PeopleIcon from '@mui/icons-material/People';
import MedicationIcon from '@mui/icons-material/Medication';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import axiosInstance from '../../axiosInstance';
import { toast } from 'react-toastify';
import { useClinic } from '../../context/ClinicContext';
import { motion } from 'framer-motion';
import { useMediaQuery } from '@mui/material';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, ChartTooltip, Legend, Filler
);

// Tarjeta de resumen con UI mejorada (misma funcionalidad)
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        borderRadius: '16px',
        boxShadow: theme.shadows[2],
        backdropFilter: 'blur(12px)',
        background: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.7)
          : alpha(theme.palette.background.paper, 0.9),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
          borderColor: alpha(color, 0.25),
        },
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography color="textSecondary" gutterBottom variant="body1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', sm: '2.4rem' } }}>
          {value}
        </Typography>
      </Box>
      <Avatar
        sx={{
          color,
          backgroundColor: alpha(color, 0.12),
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          border: `2px solid ${alpha(color, 0.2)}`
        }}
      >
        {icon}
      </Avatar>
    </Paper>
  );
};

const formatMoney = (n) =>
  new Intl.NumberFormat('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

const MotionPaper = motion(Paper);

const ClinicDashboard = () => {
  const theme = useTheme();
  const { activeClinic, selectedClinicId } = useClinic();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!selectedClinicId) return;
    const ctrl = new AbortController();

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Importante: fuerza el header X-Clinic-Id y manda clinic_id como query param
        const { data } = await axiosInstance.get('/dashboard-stats/', {
          signal: ctrl.signal,
          params: {
            t: Date.now(),
            clinic_id: selectedClinicId,
          },
          headers: {
            'X-Clinic-Id': String(selectedClinicId),
          },
        });
        setStats(data);
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        console.error(e);
        toast.error('No se pudieron cargar las estadísticas de la clínica.');
        // Fallback mínimo
        setStats({
          citas_hoy: 0,
          pacientes_hoy: 0,
          balance_financiero: 0,
          total_pacientes_clinica: 0,
          citas_por_dia: [0, 0, 0, 0, 0, 0, 0],
          top_medicos: [],
          medicamentos_frecuentes: { labels: [], data: [] },
          diagnosticos_frecuentes: { labels: [], data: [] },
          visitas_labels: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => ctrl.abort();
  }, [selectedClinicId]);

  if (!selectedClinicId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Selecciona una clínica para ver su tablero</Typography>
      </Box>
    );
  }

  if (loading || !stats) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />
      </Box>
    );
  }

  // Normalización de campos (SIN CAMBIAR tu lógica)
  const citasHoy = stats.citas_hoy ?? stats.consultas_hoy ?? 0;
  const pacientesHoy = stats.pacientes_hoy ?? stats.consultas_hoy ?? 0;
  const totalPacientes = stats.total_pacientes_clinica ?? stats.total_pacientes ?? 0;
  const balance = Number(stats.balance_financiero) || 0;  // forzar número
  const citasPorDia = stats.citas_por_dia ?? stats.visitas_diarias ?? [0, 0, 0, 0, 0, 0, 0];
  const topMedicos = Array.isArray(stats.top_medicos) ? stats.top_medicos : [];
  const meds = stats.medicamentos_frecuentes || { labels: [], data: [] };
  const dxs = stats.diagnosticos_frecuentes || { labels: [], data: [] };
  // Mapa para abreviar las etiquetas completas que envía el backend
  const abreviaturasDias = {
    'Lunes': 'Lun', 'Martes': 'Mar', 'Miércoles': 'Mié', 'Jueves': 'Jue',
    'Viernes': 'Vie', 'Sábado': 'Sáb', 'Domingo': 'Dom'
  };
  // Usar las etiquetas reales del backend (alineadas con la zona horaria de la clínica)
  const labelsCitas = Array.isArray(stats.visitas_labels) && stats.visitas_labels.length > 0
    ? stats.visitas_labels.map((d) => abreviaturasDias[d] || d)
    : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  // ⭐ CONSTRUCCIÓN SEGURA DEL BALANCE
  const storedSymbol = localStorage.getItem('currencySymbol');
  const currencySymbol = (storedSymbol || 'L').trim();
  const formattedBalance = formatMoney(balance);
  const balanceDisplayText = currencySymbol + ' ' + formattedBalance;  // concatenación básica sin template

  // Estilos de contenedor glass para secciones
  const glassCard = {
    p: { xs: 2, sm: 3 },
    borderRadius: '16px',
    boxShadow: theme.shadows[2],
    backdropFilter: 'blur(12px)',
    background: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.7)
      : alpha(theme.palette.background.paper, 0.9),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
      borderColor: alpha(theme.palette.primary.main, 0.25),
    },
  };

  // Options theme-aware para charts (no cambia datasets)
  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: { color: theme.palette.text.secondary },
        grid: { color: alpha(theme.palette.divider, 0.1) },
      },
      y: {
        beginAtZero: true,
        ticks: { color: theme.palette.text.secondary },
        grid: { color: alpha(theme.palette.divider, 0.1) },
      },
    },
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}>
      {/* Encabezado mejorado (UI) */}
      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          ...glassCard,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.secondary.main, 0.08
          )} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: isSmDown ? 44 : 54,
              height: isSmDown ? 44 : 54,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            <LocalHospitalIcon />
          </Avatar>
          <Box>
            <Typography variant={isSmDown ? 'h6' : 'h5'} sx={{ fontWeight: 800 }}>
              Tablero de la Clínica
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeClinic?.clinica?.nombre || 'Clínica'}
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Refrescar">
          <IconButton
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2), transform: 'rotate(180deg)' },
              transition: 'all 0.4s ease',
            }}
            onClick={() => window.location.reload()}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </MotionPaper>

      {/* Tarjetas de resumen (misma data) */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Citas de Hoy"
            value={citasHoy}
            icon={<EventAvailableIcon sx={{ fontSize: { xs: 28, sm: 34 } }} />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Pacientes Atendidos Hoy"
            value={pacientesHoy}
            icon={<GroupIcon sx={{ fontSize: { xs: 28, sm: 34 } }} />}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total de Pacientes"
            value={totalPacientes}
            icon={<PeopleIcon sx={{ fontSize: { xs: 28, sm: 34 } }} />}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Balance Financiero"
            value={balanceDisplayText}
            icon={<AccountBalanceWalletIcon sx={{ fontSize: { xs: 28, sm: 34 } }} />}
            color={balance >= 0 ? theme.palette.success.main : theme.palette.error.main}
          />
        </Grid>
      </Grid>

      {/* Gráficos principales - 2 COLUMNAS IGUALES con widths responsivos como DashboardPage */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 } }}>
        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={0}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            sx={{ 
              ...glassCard, 
              width: { xs: '100%', md: '1250px' }, 
              maxWidth: '100%' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Citas por día (últimos 7 días)
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 250, sm: 320 }, width: '100%' }}>
              <Line
                data={{
                  labels: labelsCitas,
                  datasets: [
                    {
                      label: 'Citas',
                      data: citasPorDia,
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      tension: 0.25,
                      fill: true,
                      borderWidth: 3,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      pointBackgroundColor: theme.palette.background.paper,
                      pointBorderColor: theme.palette.primary.main,
                      pointBorderWidth: 2,
                    },
                  ],
                }}
                options={commonChartOptions}
              />
            </Box>
          </MotionPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={0}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            sx={{ 
              ...glassCard, 
              width: { xs: '100%', md: '800px' }, 
              maxWidth: '100%' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalHospitalIcon sx={{ color: theme.palette.secondary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Médicos con más pacientes (semana)
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 250, sm: 320 }, width: '100%' }}>
              <Bar
                data={{
                  labels: topMedicos.map((m) => m.nombre || m.medico || 'Médico'),
                  datasets: [
                    {
                      label: 'Pacientes',
                      data: topMedicos.map((m) => m.pacientes || m.total || 0),
                      backgroundColor: alpha(theme.palette.secondary.main, 0.7),
                      borderColor: theme.palette.secondary.main,
                      borderWidth: 2,
                      borderRadius: 8,
                      hoverBackgroundColor: theme.palette.secondary.main,
                    },
                  ],
                }}
                options={{
                  ...commonChartOptions,
                  indexAxis: 'y',
                }}
              />
            </Box>
          </MotionPaper>
        </Grid>
      </Grid>

      {/* Gráficos de clínica: Medicamentos y Diagnósticos - 2 COLUMNAS IGUALES con widths responsivos */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={0}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            sx={{ 
              ...glassCard, 
              width: { xs: '100%', md: '1050px' }, 
              maxWidth: '100%' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MedicationIcon
                sx={{
                  fontSize: { xs: 20, sm: 24 },
                  color: theme.palette.info.main,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Medicamentos más utilizados
              </Typography>
            </Box>

            <Box sx={{ height: { xs: 240, sm: 300 }, width: '100%' }}>
              {Array.isArray(meds.labels) && meds.labels.length > 0 ? (
                <Bar
                  data={{
                    labels: meds.labels,
                    datasets: [
                      {
                        label: 'Frecuencia',
                        data: meds.data,
                        backgroundColor: alpha(theme.palette.info.main, 0.7),
                        borderColor: theme.palette.info.main,
                        borderWidth: 2,
                        borderRadius: 8,
                        hoverBackgroundColor: theme.palette.info.main,
                      },
                    ],
                  }}
                  options={{
                    ...commonChartOptions,
                    indexAxis: 'y',
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No hay datos suficientes</Typography>
                </Box>
              )}
            </Box>
          </MotionPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <MotionPaper
            elevation={0}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={{ 
              ...glassCard, 
              width: { xs: '100%', md: '1000px' }, 
              maxWidth: '100%' 
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HealthAndSafetyIcon
                sx={{
                  fontSize: { xs: 20, sm: 24 },
                  color: theme.palette.secondary.main,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Principales diagnósticos
              </Typography>
            </Box>

            <Box sx={{ height: { xs: 240, sm: 300 }, width: '100%' }}>
              {Array.isArray(dxs.labels) && dxs.labels.length > 0 ? (
                <Bar
                  data={{
                    labels: dxs.labels,
                    datasets: [
                      {
                        label: 'Frecuencia',
                        data: dxs.data,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.7),
                        borderColor: theme.palette.secondary.main,
                        borderWidth: 2,
                        borderRadius: 8,
                        hoverBackgroundColor: theme.palette.secondary.main,
                      },
                    ],
                  }}
                  options={{
                    ...commonChartOptions,
                    indexAxis: 'y',
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No hay datos suficientes</Typography>
                </Box>
              )}
            </Box>
          </MotionPaper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClinicDashboard;
